import React from "react";
import { Formik, Field, FieldProps } from "formik";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Heading,
  VStack,
  Stack,
  NumberInput,
  Image,
  NumberInputField,
  CloseButton,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@chakra-ui/react";
import axios from "axios";
import API from "../../config/api";

export default function CreatePointCard() {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();

  return (
    <Flex h="100vh" direction={"column"}>
      <Heading as="h4" size="lg" mb="4">
        {t("point_card.title.new")}
      </Heading>
      <Box bg="white" rounded="md" w={360} pb={4}>
        <Formik
          initialValues={{
            name: "",
            paid_amount: "100",
            point_acquired: "1",
            point_rate: "1",
            amount_rate: "100",
            logo: null,
            banner: null,
            url: "",
            ios_app_id: "",
            android_app_id: "",
            ios_url_scheme: "",
            status: "activate",
            redemption_unit: "¥",
          }}
          onSubmit={async (values, { setErrors }) => {
            setSubmitting(true);

            var bodyFormData = new FormData();
            Object.entries(values).forEach(([key, val]) => {
              if (key !== "logo" && key !== "banner")
                bodyFormData.append(key, val as string);
            });
            if (values.logo) {
              bodyFormData.append("logo", values.logo);
            }
            if (values.banner) {
              bodyFormData.append("banner", values.banner);
            }

            axios({
              method: "post",
              url: API.POINT_CARD,
              data: bodyFormData,
              headers: { "Content-Type": "multipart/form-data" },
            })
              .then(function (response) {
                toast({
                  title: t("point_card.api.created_success"),
                  status: "success",
                  isClosable: true,
                });
                setSubmitting(false);
                navigate(-1);
              })
              .catch(function (response) {
                //handle error
                console.log(response);
              });
          }}
        >
          {({ handleSubmit, setFieldValue, values, errors, touched }) => (
            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="flex-start">
                <FormControl
                  isRequired
                  isInvalid={!!errors.name && touched.name}
                >
                  <FormLabel htmlFor="name">{t("point_card.name")}</FormLabel>
                  <Field as={Input} id="name" name="name" />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.url && touched.url}>
                  <FormLabel htmlFor="url">{t("point_card.url")}</FormLabel>
                  <Field as={Input} id="url" name="url" />
                  <FormErrorMessage>{errors.url}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors.ios_app_id && touched.ios_app_id}
                >
                  <FormLabel htmlFor="ios_app_id">
                    {t("point_card.ios_app_id")}
                  </FormLabel>
                  <Field as={Input} id="ios_app_id" name="ios_app_id" />
                  <FormErrorMessage>{errors.ios_app_id}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors.ios_url_scheme && touched.ios_url_scheme}
                >
                  <FormLabel htmlFor="ios_url_scheme">
                    {t("point_card.ios_url_scheme")}
                  </FormLabel>
                  <Field as={Input} id="ios_url_scheme" name="ios_url_scheme" />
                  <FormErrorMessage>{errors.ios_url_scheme}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors.android_app_id && touched.android_app_id}
                >
                  <FormLabel htmlFor="android_app_id">
                    {t("point_card.android_app_id")}
                  </FormLabel>
                  <Field as={Input} id="android_app_id" name="android_app_id" />
                  <FormErrorMessage>{errors.android_app_id}</FormErrorMessage>
                </FormControl>
                <Field name="logo">
                  {({ field }: FieldProps) => {
                    return (
                      <FormControl isInvalid={!!errors.logo && touched.logo}>
                        <FormLabel htmlFor="logo">
                          {t("point_card.logo")}
                        </FormLabel>
                        <Box
                          display="flex"
                          textAlign="center"
                          justifyContent="center"
                          flexDirection="column"
                        >
                          {field.value && (
                            <Stack direction={"row"} spacing={1}>
                              <Image
                                src={URL.createObjectURL(field.value)}
                                boxSize="100px"
                                objectFit="cover"
                                alt="Logo"
                                mb={2}
                              />
                              <CloseButton
                                onClick={() => setFieldValue(field.name, null)}
                              />
                            </Stack>
                          )}
                          <input
                            name="logo"
                            accept="image/*"
                            id="logo"
                            type="file"
                            onChange={(e) => {
                              const fileReader = new FileReader();
                              fileReader.onloadend = () => {
                                if (fileReader.result instanceof ArrayBuffer) {
                                  setFieldValue(field.name, null);
                                  return;
                                }
                                if (
                                  e.target.files &&
                                  e.target.files.length === 1
                                ) {
                                  setFieldValue(field.name, e.target.files[0]);
                                }
                              };
                              if (
                                !e.target.files ||
                                e.target.files.length !== 1
                              ) {
                                setFieldValue(field.name, null);
                                return;
                              }
                              fileReader.readAsDataURL(e.target.files[0]);
                            }}
                          />
                        </Box>
                        <FormErrorMessage>{errors.logo}</FormErrorMessage>
                      </FormControl>
                    );
                  }}
                </Field>
                <Field name="banner">
                  {({ field }: FieldProps) => {
                    return (
                      <FormControl
                        isInvalid={!!errors.banner && touched.banner}
                      >
                        <FormLabel htmlFor="banner">
                          {t("payment_method.banner")}
                        </FormLabel>
                        <Box
                          display="flex"
                          textAlign="center"
                          justifyContent="center"
                          flexDirection="column"
                        >
                          {field.value && (
                            <Stack direction={"row"} spacing={1}>
                              <Image
                                src={URL.createObjectURL(field.value)}
                                boxSize="100px"
                                objectFit="cover"
                                alt="banner"
                                mb={2}
                              />
                              <CloseButton
                                onClick={() => setFieldValue(field.name, null)}
                              />
                            </Stack>
                          )}
                          <input
                            name="banner"
                            accept="image/*"
                            id="banner"
                            type="file"
                            onChange={(e) => {
                              const fileReader = new FileReader();
                              fileReader.onloadend = () => {
                                if (fileReader.result instanceof ArrayBuffer) {
                                  setFieldValue(field.name, null);
                                  return;
                                }
                                if (
                                  e.target.files &&
                                  e.target.files.length === 1
                                ) {
                                  setFieldValue(field.name, e.target.files[0]);
                                }
                              };
                              if (
                                !e.target.files ||
                                e.target.files.length !== 1
                              ) {
                                setFieldValue(field.name, null);
                                return;
                              }
                              fileReader.readAsDataURL(e.target.files[0]);
                            }}
                          />
                        </Box>
                        <FormErrorMessage>{errors.banner}</FormErrorMessage>
                      </FormControl>
                    );
                  }}
                </Field>
                <FormControl isRequired>
                  <FormLabel>{t("point_card.money_conversion")}</FormLabel>
                  <Stack direction={"row"} spacing={4} alignItems="center">
                    <Field name="paid_amount">
                      {({ field }: FieldProps) => {
                        return (
                          <NumberInput
                            value={field.value}
                            id="paid_amount"
                            name="paid_amount"
                            onChange={(val) => setFieldValue(field.name, val)}
                          >
                            <NumberInputField />
                          </NumberInput>
                        );
                      }}
                    </Field>
                    <Text w={50}>{` ¥ = `}</Text>
                    <Field name="point_acquired">
                      {({ field }: FieldProps) => {
                        return (
                          <NumberInput
                            value={field.value}
                            id="point_acquired"
                            name="point_acquired"
                            onChange={(val) => setFieldValue(field.name, val)}
                          >
                            <NumberInputField />
                          </NumberInput>
                        );
                      }}
                    </Field>
                    <Text>{`points`}</Text>
                  </Stack>
                </FormControl>
                <FormControl
                  isInvalid={
                    !!errors.redemption_unit && touched.redemption_unit
                  }
                >
                  <FormLabel htmlFor="redemption_unit">
                    {t("point_card.redemption_unit")}
                  </FormLabel>
                  <Field
                    as={Input}
                    id="redemption_unit"
                    name="redemption_unit"
                  />
                  <FormErrorMessage>{errors.redemption_unit}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>{t("point_card.point_conversion")}</FormLabel>
                  <Stack direction={"row"} spacing={4} alignItems="center">
                    <Field name="point_rate">
                      {({ field }: FieldProps) => {
                        return (
                          <NumberInput
                            value={field.value}
                            id="point_rate"
                            name="point_rate"
                            onChange={(val) => setFieldValue(field.name, val)}
                          >
                            <NumberInputField />
                          </NumberInput>
                        );
                      }}
                    </Field>
                    <Text w={130}>{` points = `}</Text>
                    <Field name="amount_rate">
                      {({ field }: FieldProps) => {
                        return (
                          <NumberInput
                            value={field.value}
                            id="amount_rate"
                            name="amount_rate"
                            onChange={(val) => setFieldValue(field.name, val)}
                          >
                            <NumberInputField />
                          </NumberInput>
                        );
                      }}
                    </Field>
                    <Text>{values.redemption_unit}</Text>
                  </Stack>
                </FormControl>
                <Stack direction="row" spacing={4} align="center">
                  <Button
                    colorScheme="blue"
                    variant="solid"
                    type="submit"
                    isLoading={submitting}
                    loadingText={t("action.loading.creating")}
                  >
                    {t("action.create")}
                  </Button>
                  <Button
                    colorScheme="blue"
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                  >
                    {t("action.cancel")}
                  </Button>
                </Stack>
              </VStack>
            </form>
          )}
        </Formik>
      </Box>
    </Flex>
  );
}
